import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiChevronDown, mdiChevronRight } from '@mdi/js'
import { SECTION_ICONS } from '../../lib/section-icons'
import { AppIcon } from '../ui/icon'
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  closestCenter,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { cn } from '../../lib/utils'
import { CONDITION_META } from '../../lib/capex-utils'
import { useAsset } from '../../hooks/useQueries'
import { useFormatters } from '../../lib/format'
import { useCan } from '../../contexts/PermissionsContext'
import type { Worksite, EquipmentCondition } from './types'
import { WorksiteCell, EmptyCell } from './WorksiteCell'
import { PackageRow } from './PackageRow'

interface WorksiteRowProps {
  worksite: Worksite
  horizonStart: number
  horizonEnd: number
  org: string
  isReadOnly: boolean
  onUpdate: (uid: string, body: Record<string, unknown>, ifMatch: string) => void
  onDelete: (uid: string) => void
  onQuote?: (pkg: import('./types').WorkPackage) => void
  onDeleteQuote?: (pkg: import('./types').WorkPackage) => void
  onAddPackage?: (worksiteUid: string) => void
  onEditWorksite?: (worksite: Worksite) => void
  onEditPackage?: (pkg: import('./types').WorkPackage) => void
}

function DraggableWorksiteCell({
  worksite,
  horizonStart,
  horizonEnd,
  isReadOnly,
  onUpdate,
  onDelete,
  onAddPackage,
  onEditWorksite,
  isDraggingActive,
}: {
  worksite: Worksite
  horizonStart: number
  horizonEnd: number
  isReadOnly: boolean
  onUpdate: (uid: string, body: Record<string, unknown>, ifMatch: string) => void
  onDelete: (uid: string) => void
  onAddPackage?: (worksiteUid: string) => void
  onEditWorksite?: (worksite: Worksite) => void
  isDraggingActive: boolean
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `drag-${worksite.uid}`,
    data: { worksite },
    disabled: isReadOnly,
  })

  // Also register as a droppable so the user can drop back to the same year
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `drop-${worksite.uid}-${worksite.year}`,
    data: { year: worksite.year, worksiteUid: worksite.uid },
    disabled: !isDraggingActive || isReadOnly,
  })

  return (
    <div
      ref={(node) => {
        setNodeRef(node)
        setDropRef(node)
      }}
      className={cn(isDraggingActive && isOver && 'ring-1 ring-primary/30 rounded-sm')}
      {...listeners}
      {...attributes}
    >
      <WorksiteCell
        worksite={worksite}
        horizonStart={horizonStart}
        horizonEnd={horizonEnd}
        isReadOnly={isReadOnly}
        onUpdate={onUpdate}
        onDelete={onDelete}
        onAddPackage={onAddPackage}
        onEditWorksite={onEditWorksite}
        isDragging={isDragging}
      />
    </div>
  )
}

export const WorksiteRow = React.memo(function WorksiteRow({
  worksite,
  horizonStart,
  horizonEnd,
  org,
  isReadOnly,
  onUpdate,
  onDelete,
  onQuote,
  onDeleteQuote,
  onAddPackage,
  onEditWorksite,
  onEditPackage,
}: WorksiteRowProps) {
  const { t } = useTranslation('capex')
  const [expanded, setExpanded] = useState(false)
  const [isDraggingActive, setIsDraggingActive] = useState(false)

  const years: number[] = []
  for (let y = horizonStart; y <= horizonEnd; y++) {
    years.push(y)
  }

  const { formatMoney } = useFormatters()
  const canReadContracts = useCan('contracts.read')
  const unlocked = canReadContracts ? worksite.unlocked_rent : undefined
  const packages = worksite.work_packages ?? []
  const condition = worksite.condition as EquipmentCondition | undefined
  const conditionMeta = condition ? CONDITION_META[condition] : undefined

  const { data: linkedAsset } = useAsset(org, worksite.asset_uid || '')

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  )

  const handleDragStart = useCallback((_event: DragStartEvent) => {
    setIsDraggingActive(true)
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setIsDraggingActive(false)
      const { over } = event
      if (!over) return

      const targetYear = over.data.current?.year as number | undefined
      if (targetYear == null || targetYear === worksite.year) return

      const ifMatch = worksite.updated_at ?? ''
      const body: Record<string, unknown> = { year: targetYear }
      const originalYear = worksite.deferred_from_year ?? worksite.year ?? targetYear

      if (worksite.status !== 'validated') {
        if (targetYear > originalYear) {
          body.status = 'deferred'
          body.deferred_from_year = originalYear
        } else {
          // At or before the original year — clear deferred state
          body.status = 'draft'
          body.deferred_from_year = null
        }
      }

      onUpdate(worksite.uid!, body, ifMatch)
    },
    [worksite, onUpdate]
  )

  const handleDragCancel = useCallback(() => {
    setIsDraggingActive(false)
  }, [])

  return (
    <>
      <tr
        className="hover:bg-muted/20 border-b border-border/50"
        data-testid={`worksite-row-${worksite.uid}`}
      >
        {/* Name cell — sticky left */}
        <td className="sticky left-0 z-10 bg-background border-r border-border px-3 py-2 w-80 min-w-80">
          <div className="flex items-center gap-2">
            {/* Expand toggle */}
            {packages.length > 0 && (
              <button
                type="button"
                className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setExpanded((e) => !e)}
                data-testid={`worksite-expand-${worksite.uid}`}
                aria-expanded={expanded}
                aria-label={expanded ? 'Collapse packages' : 'Expand packages'}
              >
                {expanded ? (
                  <AppIcon path={mdiChevronDown} size="xs" tone="muted" />
                ) : (
                  <AppIcon path={mdiChevronRight} size="xs" tone="muted" />
                )}
              </button>
            )}
            {packages.length === 0 && (
              <span className="shrink-0 w-3.5 h-3.5 inline-block" />
            )}

            {/* Worksite info */}
            <div className="flex-1 min-w-0 space-y-0.5">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-sm font-medium text-foreground truncate">
                  {worksite.name}
                </span>
              </div>
              {unlocked && unlocked.lease_count > 0 && (
                <div
                  className="text-xs text-muted-foreground"
                  data-testid={`worksite-unlocked-rent-${worksite.uid}`}
                >
                  {t('worksite.unlocked_rent')}: {formatMoney(unlocked.rent_at_risk, unlocked.currency)}
                  {unlocked.rent_unverified > 0 &&
                    ` (+${formatMoney(unlocked.rent_unverified, unlocked.currency)} ${t('kpi.unlocked_rent_unverified')})`}
                </div>
              )}
              <div className="flex items-center gap-1 flex-wrap">
                {conditionMeta && (
                  <span
                    className={cn(
                      'text-xs px-1 py-0.5 rounded font-medium',
                      conditionMeta.color
                    )}
                    aria-label={`Condition: ${t(`condition.${condition}`)}`}
                    data-testid={`worksite-condition-${worksite.uid}`}
                  >
                    {t(`condition.${condition}`)}
                  </span>
                )}
                {worksite.sub && (
                  <span className="text-xs text-muted-foreground truncate">
                    {worksite.sub}
                  </span>
                )}
              </div>
              {worksite.asset_uid && (
                <Link
                  to="/$org/assets/$assetUid"
                  params={{ org, assetUid: worksite.asset_uid }}
                  className="flex items-center gap-1 text-xs text-primary hover:underline w-fit"
                  data-testid={`worksite-asset-link-${worksite.uid}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <AppIcon path={SECTION_ICONS.assets} size="xs" className="shrink-0" />
                  <span className="truncate">
                    {linkedAsset?.name || t('worksite.asset_unknown')}
                  </span>
                </Link>
              )}
            </div>
          </div>
        </td>

        {/* Year cells — wrapped in DndContext per row */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          {years.map((year) => (
            <td
              key={year}
              className="border-r border-border/30 px-1 py-1 align-middle"
            >
              {year === worksite.year ? (
                <DraggableWorksiteCell
                  worksite={worksite}
                  horizonStart={horizonStart}
                  horizonEnd={horizonEnd}
                  isReadOnly={isReadOnly}
                  onUpdate={onUpdate}
                  onDelete={onDelete}
                  onAddPackage={onAddPackage}
                  onEditWorksite={onEditWorksite}
                  isDraggingActive={isDraggingActive}
                />
              ) : (
                <EmptyCell
                  year={year}
                  isDropTarget={isDraggingActive && !isReadOnly}
                  worksiteUid={worksite.uid}
                />
              )}
            </td>
          ))}

          <DragOverlay>
            {isDraggingActive && (
              <div className="opacity-80 pointer-events-none">
                <WorksiteCell
                  worksite={worksite}
                  horizonStart={horizonStart}
                  horizonEnd={horizonEnd}
                  isReadOnly={true}
                  onUpdate={() => {}}
                  onDelete={() => {}}
                />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </tr>

      {/* Package rows — shown when expanded */}
      {expanded &&
        packages.map((pkg) => (
          <PackageRow
            key={pkg.uid}
            pkg={pkg}
            worksiteYear={worksite.year ?? 0}
            horizonStart={horizonStart}
            horizonEnd={horizonEnd}
            org={org}
            isReadOnly={isReadOnly}
            onQuote={onQuote}
            onDeleteQuote={onDeleteQuote}
            onEditPackage={onEditPackage}
          />
        ))}
    </>
  )
})
